import type {WorkloadInput} from "./model/WorkloadInput.ts";
import type {Workload} from "./model/Workload.ts";

// First we try to scan jobs through links, if any of the jobs is a variable (i.e. matches "${...}") then we resort to
//  looking through the most recent 4 attached tasks
function identifyJobs(workload: Workload, input: WorkloadInput) {
    const jobs = new Set<string>()
    let hasVars = false

    // First we just recursively go through the jobs from the starting job
    const queue = [workload.startingJob]
    while(queue.length !== 0) {
        const jobName = queue.shift()
        if(!jobName) continue
        if(jobs.has(jobName)) continue

        //Check for variables
        if(jobName.match("\\$\\{.*\\}")) {
            hasVars = true
            continue
        }

        const job = input.jobsByName[jobName]
        if(job) {
            jobs.add(jobName)
            job.linksTo.forEach(x => queue.push(x))
        }
    }

    // If we have variables, we need to look at the most recent 4 tasks to see if we can work out what the var resolves to
    if(hasVars) {
        const tasksToScan = workload.tasks.slice(-4)
        tasksToScan.forEach(task => {
            task.jobsRun.forEach(x => jobs.add(x))
        })
    }

    workload.connectedJobs = [...jobs.values()]
}

export function scanWorkloads(input: WorkloadInput): Workload[] {
    const workloads: Workload[] = []

    function findWorkload(versionName: string, jobName: string): Workload | undefined {
        return workloads.find(x => x.versionId.versionName === versionName && x.startingJob === jobName)
    }

    // Firstly, just group schedules in to raw workloads, we'll fill them in later
    input.schedules.forEach((schedule) => {
        const startingJob = schedule.jobName
        const existing = findWorkload(schedule.versionName, startingJob)

        if(existing) {
            existing.schedules.push(schedule)
        } else {
            workloads.push({
                versionId: {...input.projectId, versionName: schedule.versionName},
                startingJob,
                connectedJobs: [],
                schedules: [schedule],
                tasks: [],
                failureCount: 0,
                successCount: 0
            })
        }
    })

    input.taskHistory.forEach((task) => {
        if(task.batchInfo.source === "manual") return // no manual ones here thx
        const existing = findWorkload(task.batchInfo.versionName, task.batchInfo.jobName)
        if(existing) {
            existing.tasks.push(task)
        } else {
            workloads.push({
                versionId: {...input.projectId, versionName: task.batchInfo.versionName},
                startingJob: task.batchInfo.jobName,
                connectedJobs: [],
                schedules: [],
                tasks: [task],
                failureCount: 0,
                successCount: 0
            })
        }
    })

    workloads.forEach(workload => identifyJobs(workload, input))

    return workloads
}